/**
 * Componente DealKanbanFilters - Header com busca expansível e filtros rápidos
 * Layout inline single-row: Título + Lupa + | + Filtros rápidos compactos + Ações
 * Versão: 3.2.0
 * RELEASE: v3.2.0 - Adicionado filtro multi-seleção de tags no Sheet de Filtros Avançados
 * RELEASE: v2.2.0 - CategoryFilterSelect + LEAD_SOURCES
 * RELEASE: v3.0.0 - Reestruturado para single-row, filtros compactos h-[22px] (padrão Activities)
 */
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, SlidersHorizontal, X, Calendar, Users, 
  Tag, Package, FolderTree, Briefcase, ChevronDown, RefreshCw, Plus, ArrowRightLeft, Zap, List 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn, parseDateLocal } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Separator } from '@/components/ui/separator';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuTrigger, DropdownMenuSeparator 
} from '@/components/ui/dropdown-menu';
import { useDealFilterOptions } from '@/hooks/useDealFilterOptions';
import { CrmSellerFilter } from '@/components/crm/filters/CrmSellerFilter';
import { CrmUserFilter } from '@/components/crm/filters/CrmUserFilter';
import { DealFilters, DealPriority, DealStatus } from '@/hooks/useCrmDeals';
import { CategoryFilterSelect } from '@/components/crm/CategoryFilterSelect';
import { getLeadSourceLabel, getLeadSourceIcon } from '@/lib/lead-sources';
import { useLeadSources } from '@/hooks/useLeadSources';
// v3.1.0 (2026-07-11): Filtros multi-seleção reutilizados da listagem
import { CrmDealSourceFilter } from '@/components/crm/filters/CrmDealSourceFilter';
import { CrmLossReasonFilter } from '@/components/crm/filters/CrmLossReasonFilter';
// v3.2.0 (2026-08-28): Filtro multi-seleção de tags reutilizado da listagem
import { CrmDealTagsFilter } from '@/components/crm/filters/CrmDealTagsFilter';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Interface de props do componente
interface DealKanbanFiltersProps {
  /** Nome da pipeline selecionada para exibir no título */
  pipelineName: string;
  /** Filtros ativos */
  filters: DealFilters;
  /** Callback para atualizar filtros */
  onFiltersChange: (filters: DealFilters) => void;
  /** Callback para adicionar novo negócio */
  onNewDeal?: () => void;
  /** Callback para atualizar/refresh */
  onRefresh?: () => void;
  /** Callback para abrir modal de transferência em massa */
  onBulkTransfer?: () => void;
  /** Callback para abrir modal de regras de automação */
  onAutomationRules?: () => void;
  /** Indica se há automações configuradas no pipeline */
  hasAutomations?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Componente de header do Kanban de Deals
 * Layout: Ícone + Título + Lupa Expansível + Filtros Rápidos + Avançados + Novo Negócio
 */
export function DealKanbanFilters({
  pipelineName,
  filters,
  onFiltersChange,
  onNewDeal,
  onRefresh,
  onBulkTransfer,
  onAutomationRules,
  hasAutomations = false,
  className,
}: DealKanbanFiltersProps) {
  // Navegação para listagem de negócios
  const navigate = useNavigate();
  // Estado para busca expansível (sanfona)
  // v3.1.0: busca começa recolhida; expande somente ao clicar na lupa (ou quando filtro de busca já está ativo)
  const [isSearchExpanded, setIsSearchExpanded] = useState(() => Boolean(filters.search));
  const [searchValue, setSearchValue] = useState(filters.search || '');
  
  // Estado para popover de filtros avançados
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  
  // Estado para calendários no popover avançado
  const [showDateFromCalendar, setShowDateFromCalendar] = useState(false);
  const [showDateToCalendar, setShowDateToCalendar] = useState(false);
  // v3.0.0: Estado para calendários dos novos filtros de data
  const [showCreatedFromCal, setShowCreatedFromCal] = useState(false);
  const [showCreatedToCal, setShowCreatedToCal] = useState(false);
  const [showWonFromCal, setShowWonFromCal] = useState(false);
  const [showWonToCal, setShowWonToCal] = useState(false);
  const [showLostFromCal, setShowLostFromCal] = useState(false);
  const [showLostToCal, setShowLostToCal] = useState(false);
  const [showPhaseUpdFromCal, setShowPhaseUpdFromCal] = useState(false);
  const [showPhaseUpdToCal, setShowPhaseUpdToCal] = useState(false);

  // Opções dos filtros
  const { users, sellers, sources, categories, products, isLoading } = useDealFilterOptions();
  // Catálogo dinâmico de origens (sistema + custom do tenant)
  const { activeSources: leadSourceCatalog } = useLeadSources();

  // Debounce da busca (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== filters.search) {
        onFiltersChange({ ...filters, search: searchValue || undefined });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Sincroniza busca externa
  useEffect(() => {
    if (filters.search !== searchValue) {
      setSearchValue(filters.search || '');
    }
  }, [filters.search]);

  // Fecha busca ao clicar fora (se vazio)
  const handleSearchBlur = () => {
    if (!searchValue) {
      setIsSearchExpanded(false);
    }
  };

  // Toggle da busca
  const toggleSearch = () => {
    if (isSearchExpanded && !searchValue) {
      setIsSearchExpanded(false);
    } else {
      setIsSearchExpanded(true);
    }
  };

  // Conta filtros avançados ativos (exceto busca, status, seller, probability, created)
  const advancedFilterCount = useMemo(() => {
    let count = 0;
    if (filters.assigned_to) count++;
    // v3.2.0: Tags entram no badge do botão "Mais"
    if (filters.tag_ids && filters.tag_ids.length > 0) count++;
    if (filters.source || (filters.source_ids && filters.source_ids.length > 0)) count++;
    if (filters.loss_reason_ids && filters.loss_reason_ids.length > 0) count++;
    if (filters.category_id) count++;
    if (filters.product_id) count++;
    if (filters.priority) count++;
    if (filters.date_from || filters.date_to) count++;
    // v3.0.0: Novos filtros de data avançados
    if (filters.won_date_from || filters.won_date_to) count++;
    if (filters.lost_date_from || filters.lost_date_to) count++;
    if (filters.phase_updated_from || filters.phase_updated_to) count++;
    return count;
  }, [filters]);

  // Conta total de filtros ativos (para indicador visual)
  const totalFilterCount = useMemo(() => {
    let count = advancedFilterCount;
    if (filters.seller_id) count++;
    if (filters.status) count++;
    if (filters.probability_min !== undefined || filters.probability_max !== undefined) count++;
    // v3.0.0: Filtro rápido de data de criação
    if (filters.created_from || filters.created_to) count++;
    return count;
  }, [filters, advancedFilterCount]);

  // Limpa todos os filtros
  const handleClearAllFilters = () => {
    onFiltersChange({ search: filters.search }); // Mantém apenas a busca
  };

  // Atualiza filtro específico
  const updateFilter = (key: keyof DealFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value || undefined });
  };

  // Labels de status
  const statusLabels: Record<DealStatus, string> = {
    open: 'Aberto',
    won: 'Ganho',
    lost: 'Perdido',
  };

  // Labels de prioridade
  const priorityLabels: Record<DealPriority, string> = {
    low: 'Baixa',
    medium: 'Média',
    high: 'Alta',
    urgent: 'Urgente',
  };

  // Label do status selecionado
  const selectedStatusLabel = useMemo(() => {
    if (!filters.status) return 'Status';
    return statusLabels[filters.status as DealStatus] || 'Status';
  }, [filters.status]);

  // Label da probabilidade
  const probabilityLabel = useMemo(() => {
    if (filters.probability_min === undefined && filters.probability_max === undefined) {
      return 'Probabilidade';
    }
    const min = filters.probability_min ?? 0;
    const max = filters.probability_max ?? 100;
    return `${min}% - ${max}%`;
  }, [filters.probability_min, filters.probability_max]);

  return (
    <div className={cn('flex items-center gap-2 sm:gap-3 w-full', className)}>
      {/* Ícone + Título da Pipeline */}
      <div className="flex items-center gap-2 shrink-0">
        <Briefcase className="h-5 w-5 text-primary" />
        <h1 className="text-lg font-semibold truncate max-w-[100px] sm:max-w-[160px]">
          {pipelineName || 'Pipeline'}
        </h1>
      </div>

      {/* Busca Expansível (Sanfona) */}
      <div className="flex items-center shrink-0">
        <div 
          className={cn(
            "flex items-center overflow-hidden transition-all duration-300 ease-out",
            isSearchExpanded ? "w-32 sm:w-44 opacity-100 mr-1" : "w-0 opacity-0"
          )}
        >
          <div className="relative w-full">
            <Input
              placeholder="Buscar..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onBlur={handleSearchBlur}
              className="h-7 w-full text-xs rounded-full pl-3 pr-7"
              autoFocus={isSearchExpanded}
            />
            {searchValue && (
              <button
                onClick={() => { setSearchValue(''); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="icon"
          className="h-7 w-7 rounded-full shrink-0"
          onClick={toggleSearch}
        >
          {isSearchExpanded ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>

      {/* Separador visual (desktop) */}
      <Separator orientation="vertical" className="h-5 hidden sm:block shrink-0" />

      {/* Filtros rápidos inline (single-row, padrão Activities v3.0.0) */}
      <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide shrink-0 min-w-0">

      {/* Filtro Rápido: Vendedor (componente padronizado com busca e avatar) */}
      <CrmSellerFilter
        value={filters.seller_id || null}
        onChange={(id) => updateFilter('seller_id', id)}
        className="h-[22px] text-xs px-2"
      />

      {/* Filtro Rápido: Probabilidade */}
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className={cn(
              "h-[22px] px-2 text-xs gap-1.5",
              (filters.probability_min !== undefined || filters.probability_max !== undefined) && "border-primary bg-primary/5"
            )}
          >
            <span>{probabilityLabel}</span>
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3" align="start">
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Probabilidade</span>
              <span className="font-medium">
                {filters.probability_min ?? 0}% - {filters.probability_max ?? 100}%
              </span>
            </div>
            <Slider
              min={0}
              max={100}
              step={10}
              value={[filters.probability_min ?? 0, filters.probability_max ?? 100]}
              onValueChange={([min, max]) => {
                onFiltersChange({
                  ...filters,
                  probability_min: min === 0 ? undefined : min,
                  probability_max: max === 100 ? undefined : max,
                });
              }}
            />
            {(filters.probability_min !== undefined || filters.probability_max !== undefined) && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full h-7 text-xs"
                onClick={() => onFiltersChange({ ...filters, probability_min: undefined, probability_max: undefined })}
              >
                Limpar
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Filtro Rápido: Status */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className={cn(
              "h-[22px] px-2 text-xs gap-1.5",
              filters.status && "border-primary bg-primary/5"
            )}
          >
            <span>{selectedStatusLabel}</span>
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem 
            onClick={() => updateFilter('status', null)}
            className={cn(!filters.status && "bg-accent")}
          >
            Todos
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {Object.entries(statusLabels).map(([value, label]) => (
            <DropdownMenuItem 
              key={value}
              onClick={() => updateFilter('status', value)}
              className={cn(filters.status === value && "bg-accent")}
            >
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* v3.0.0: Filtro Rápido: Data de Criação */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-[22px] px-2 text-xs gap-1.5",
              (filters.created_from || filters.created_to) && "border-primary bg-primary/5"
            )}
          >
            <Calendar className="h-3 w-3" />
            <span>
              {filters.created_from || filters.created_to
                ? `${filters.created_from ? format(parseDateLocal(filters.created_from), 'dd/MM', { locale: ptBR }) : '...'} - ${filters.created_to ? format(parseDateLocal(filters.created_to), 'dd/MM', { locale: ptBR }) : '...'}`
                : 'Criação'}
            </span>
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <div className="space-y-2">
            <span className="text-xs text-muted-foreground font-medium">Data de Criação</span>
            <div className="flex gap-2">
              <Popover open={showCreatedFromCal} onOpenChange={setShowCreatedFromCal}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
                    {filters.created_from
                      ? format(parseDateLocal(filters.created_from), 'dd/MM/yy', { locale: ptBR })
                      : 'De'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={filters.created_from ? parseDateLocal(filters.created_from) : undefined}
                    onSelect={(date) => {
                      updateFilter('created_from', date ? format(date, 'yyyy-MM-dd') : null);
                      setShowCreatedFromCal(false);
                    }}
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
              <Popover open={showCreatedToCal} onOpenChange={setShowCreatedToCal}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
                    {filters.created_to
                      ? format(parseDateLocal(filters.created_to), 'dd/MM/yy', { locale: ptBR })
                      : 'Até'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={filters.created_to ? parseDateLocal(filters.created_to) : undefined}
                    onSelect={(date) => {
                      updateFilter('created_to', date ? format(date, 'yyyy-MM-dd') : null);
                      setShowCreatedToCal(false);
                    }}
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>
            {(filters.created_from || filters.created_to) && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-7 text-xs"
                onClick={() => onFiltersChange({ ...filters, created_from: undefined, created_to: undefined })}
              >
                Limpar
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Filtros Avançados (Sheet lateral) */}
      <Button 
        variant="outline" 
        size="sm"
        className="h-[22px] px-2 text-xs gap-1.5"
        onClick={() => setIsAdvancedOpen(true)}
      >
        <SlidersHorizontal className="h-3 w-3" />
        <span>Mais</span>
        {advancedFilterCount > 0 && (
          <Badge variant="secondary" className="h-4 px-1 text-[10px] ml-0.5 bg-primary/15 text-primary border-0">
            {advancedFilterCount}
          </Badge>
        )}
      </Button>

      {/* Botão Limpar Todos os Filtros - visível apenas quando há filtros ativos */}
      {totalFilterCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-[22px] px-2 text-xs gap-1 text-muted-foreground hover:text-destructive"
          onClick={handleClearAllFilters}
          title="Limpar todos os filtros"
        >
          <X className="h-3.5 w-3.5" />
          <Badge variant="secondary" className="h-4 px-1 text-[10px]">
            {totalFilterCount}
          </Badge>
        </Button>
      )}
      </div>

      {/* Spacer para empurrar botões para direita */}
      <div className="flex-1 min-w-0" />

      {/* Botões de ação (Automação + Transferência + Atualizar + Novo Negócio) */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Botão Regras de Automação */}
        {onAutomationRules && (
          <Button 
            variant="outline" 
            size="icon-sm"
            className={cn(
              hasAutomations && "text-amber-500"
            )}
            onClick={onAutomationRules}
            title="Regras de Automação"
          >
            <Zap className="h-3.5 w-3.5" />
          </Button>
        )}
        {/* Botão Transferência em Massa */}
        {onBulkTransfer && (
          <Button 
            variant="outline" 
            size="icon-sm"
            onClick={onBulkTransfer}
            title="Transferência em Massa"
          >
            <ArrowRightLeft className="h-3.5 w-3.5" />
          </Button>
        )}
        {onRefresh && (
          <Button 
            variant="outline" 
            size="icon-sm"
            onClick={onRefresh}
            title="Atualizar dados"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        )}
        {/* Botão atalho para listagem de negócios */}
        <Button
          variant="outline"
          size="icon-sm"
          onClick={() => navigate('/tenant/crm/deals')}
          title="Todos os Negócios"
        >
          <List className="h-3.5 w-3.5" />
        </Button>
        {onNewDeal && (
          <Button 
            size="sm"
            className="h-7 px-2 sm:px-3"
            onClick={onNewDeal}
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline ml-1">Novo Negócio</span>
          </Button>
        )}
      </div>
      <Sheet open={isAdvancedOpen} onOpenChange={setIsAdvancedOpen}>
        <SheetContent side="right" className="w-80 p-0 flex flex-col">
          <SheetHeader className="p-4 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-sm">Filtros Avançados</SheetTitle>
              {advancedFilterCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 text-xs px-2"
                  onClick={handleClearAllFilters}
                >
                  Limpar
                </Button>
              )}
            </div>
          </SheetHeader>

          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">

            {/* Responsável (componente padronizado com busca e avatar) */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Users className="h-3 w-3" />
                Responsável
              </Label>
              <CrmUserFilter
                value={filters.assigned_to || null}
                onChange={(id) => updateFilter('assigned_to', id)}
                className="h-8 text-xs w-full"
              />
            </div>

            {/* v3.2.0: Tags do negócio (multi-seleção, reutiliza padrão da listagem) */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Tag className="h-3 w-3" />
                Tags
              </Label>
              <CrmDealTagsFilter
                value={filters.tag_ids || []}
                onChange={(ids) => updateFilter('tag_ids', ids.length > 0 ? ids : null)}
                className="w-full h-8 text-xs justify-start"
              />
            </div>

            {/* Prioridade */}
            <div className="space-y-1.5">
              <Label className="text-xs">Prioridade</Label>
              <Select
                value={filters.priority || 'all'}
                onValueChange={(v) => updateFilter('priority', v === 'all' ? null : v)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {Object.entries(priorityLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Origem - v3.1.0 (2026-07-11): multi-seleção via CrmDealSourceFilter */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Tag className="h-3 w-3" />
                Origem
              </Label>
              <CrmDealSourceFilter
                value={filters.source_ids || []}
                onChange={(ids) => updateFilter('source_ids', ids.length > 0 ? ids : null)}
                className="w-full h-8 text-xs justify-start"
              />
            </div>

            {/* Motivos de Perda - v3.1.0 (2026-07-11): multi-seleção */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Tag className="h-3 w-3" />
                Motivos de Perda
              </Label>
              <CrmLossReasonFilter
                value={filters.loss_reason_ids || []}
                onChange={(ids) => updateFilter('loss_reason_ids', ids.length > 0 ? ids : null)}
                className="w-full h-8 text-xs justify-start"
              />
            </div>

            {/* Categoria - hierárquico */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <FolderTree className="h-3 w-3" />
                Categoria
              </Label>
              <CategoryFilterSelect
                value={filters.category_id || null}
                onChange={(id) => updateFilter('category_id', id)}
                placeholder="Todas"
                size="xs"
                className="w-full"
              />
            </div>

            {/* Produto */}
            {products.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <Package className="h-3 w-3" />
                  Produto
                </Label>
                <Select
                  value={filters.product_id || 'all'}
                  onValueChange={(v) => updateFilter('product_id', v === 'all' ? null : v)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {products.map((prod) => (
                      <SelectItem key={prod.id} value={prod.id}>{prod.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Data prevista de fechamento */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Calendar className="h-3 w-3" />
                Data Prevista Fechamento
              </Label>
              <div className="flex gap-2">
                <Popover open={showDateFromCalendar} onOpenChange={setShowDateFromCalendar}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
                      {filters.date_from 
                        ? format(parseDateLocal(filters.date_from), 'dd/MM/yy', { locale: ptBR })
                        : 'De'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={filters.date_from ? parseDateLocal(filters.date_from) : undefined}
                      onSelect={(date) => {
                        updateFilter('date_from', date ? format(date, 'yyyy-MM-dd') : null);
                        setShowDateFromCalendar(false);
                      }}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>

                <Popover open={showDateToCalendar} onOpenChange={setShowDateToCalendar}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
                      {filters.date_to 
                        ? format(parseDateLocal(filters.date_to), 'dd/MM/yy', { locale: ptBR })
                        : 'Até'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={filters.date_to ? parseDateLocal(filters.date_to) : undefined}
                      onSelect={(date) => {
                        updateFilter('date_to', date ? format(date, 'yyyy-MM-dd') : null);
                        setShowDateToCalendar(false);
                      }}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* v3.0.0: Filtro Data Ganho */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Calendar className="h-3 w-3" />
                Data Ganho
              </Label>
              <div className="flex gap-2">
                <Popover open={showWonFromCal} onOpenChange={setShowWonFromCal}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
                      {filters.won_date_from
                        ? format(parseDateLocal(filters.won_date_from), 'dd/MM/yy', { locale: ptBR })
                        : 'De'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={filters.won_date_from ? parseDateLocal(filters.won_date_from) : undefined}
                      onSelect={(date) => {
                        updateFilter('won_date_from', date ? format(date, 'yyyy-MM-dd') : null);
                        setShowWonFromCal(false);
                      }}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
                <Popover open={showWonToCal} onOpenChange={setShowWonToCal}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
                      {filters.won_date_to
                        ? format(parseDateLocal(filters.won_date_to), 'dd/MM/yy', { locale: ptBR })
                        : 'Até'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={filters.won_date_to ? parseDateLocal(filters.won_date_to) : undefined}
                      onSelect={(date) => {
                        updateFilter('won_date_to', date ? format(date, 'yyyy-MM-dd') : null);
                        setShowWonToCal(false);
                      }}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* v3.0.0: Filtro Data Perdido */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Calendar className="h-3 w-3" />
                Data Perdido
              </Label>
              <div className="flex gap-2">
                <Popover open={showLostFromCal} onOpenChange={setShowLostFromCal}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
                      {filters.lost_date_from
                        ? format(parseDateLocal(filters.lost_date_from), 'dd/MM/yy', { locale: ptBR })
                        : 'De'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={filters.lost_date_from ? parseDateLocal(filters.lost_date_from) : undefined}
                      onSelect={(date) => {
                        updateFilter('lost_date_from', date ? format(date, 'yyyy-MM-dd') : null);
                        setShowLostFromCal(false);
                      }}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
                <Popover open={showLostToCal} onOpenChange={setShowLostToCal}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
                      {filters.lost_date_to
                        ? format(parseDateLocal(filters.lost_date_to), 'dd/MM/yy', { locale: ptBR })
                        : 'Até'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={filters.lost_date_to ? parseDateLocal(filters.lost_date_to) : undefined}
                      onSelect={(date) => {
                        updateFilter('lost_date_to', date ? format(date, 'yyyy-MM-dd') : null);
                        setShowLostToCal(false);
                      }}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* v3.0.0: Filtro Data Atualização Fase */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Calendar className="h-3 w-3" />
                Data Atualização Fase
              </Label>
              <div className="flex gap-2">
                <Popover open={showPhaseUpdFromCal} onOpenChange={setShowPhaseUpdFromCal}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
                      {filters.phase_updated_from
                        ? format(parseDateLocal(filters.phase_updated_from), 'dd/MM/yy', { locale: ptBR })
                        : 'De'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={filters.phase_updated_from ? parseDateLocal(filters.phase_updated_from) : undefined}
                      onSelect={(date) => {
                        updateFilter('phase_updated_from', date ? format(date, 'yyyy-MM-dd') : null);
                        setShowPhaseUpdFromCal(false);
                      }}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
                <Popover open={showPhaseUpdToCal} onOpenChange={setShowPhaseUpdToCal}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
                      {filters.phase_updated_to
                        ? format(parseDateLocal(filters.phase_updated_to), 'dd/MM/yy', { locale: ptBR })
                        : 'Até'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={filters.phase_updated_to ? parseDateLocal(filters.phase_updated_to) : undefined}
                      onSelect={(date) => {
                        updateFilter('phase_updated_to', date ? format(date, 'yyyy-MM-dd') : null);
                        setShowPhaseUpdToCal(false);
                      }}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

    </div>
  );
}
